import { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MoneyInput } from '@/components/ui/money-input';
import { RadioGroup } from '@/components/ui/radio-group';
import { Select } from '@/components/ui/select';
import { useToast } from '@/components/ToastProvider';
import { errorCode, errorMessage } from '@/lib/errors';
import { formatMoney } from '@/lib/format';
import { OPERATION_CONFLICT_MESSAGE, useInvalidateFinance, useSettleScheduleEntry } from '@/lib/finance';
import { formatMinor, fromMinor, toMinor } from '@/lib/money';
import { isHighlighted, needsSecondPress, settleOutcome } from '@/lib/planDeviation';
import {
  arrivedMinor,
  canSubmitSingle,
  draftDifferenceMinor,
  draftLineInputs,
  draftReady,
  feeArrivedMinor,
  feeGapMinor,
  liveLines,
  sameContext,
  staleFieldError,
  singleBankMinor,
  singleSettleMinor,
  targetComparison,
  type DraftContext,
  type DraftEvent,
  type TargetKey,
} from '@/lib/settleDraft';
import { AttachmentList } from './AttachmentList';
import { ReceiptUploadButton } from './ReceiptUploadButton';
import { offPlanText, outcomeText } from './planOutcomeText';
import { DraftConflictPanel, DraftLinesEditor, DraftStalePanel, DraftSuggestionInfo } from './SettleDraftPanels';
import { useConfirmTwice } from './useConfirmTwice';
import { useSettleDraft } from './useSettleDraft';
import { SETTLE_REMAINDER_OPTIONS, useSettleForm, useSettleOperationId, type SettleRemainder } from './useSettleForm';
import type { FinanceSettleRequest } from '@/api/generated/types.gen';
import type { PaymentScheduleEntry } from '@/lib/payments';

export interface ConfirmReceiptDialogProps {
  open: boolean;
  onClose: () => void;
  bookingId: string;
  entry: PaymentScheduleEntry;
  businessTz?: string;
}

// The fourth answer to «below plan — what about the rest?» (OH-193, owner's
// decision 2026-10-20): the bank kept the difference as a fee. Not a wire
// remainder mode — it sends no `remainder` at all: the target settles in full
// and the server's shortfall line closes the gap.
type Mode = SettleRemainder | 'fee';
const FEE_OPTION = { value: 'fee' as const, label: 'The bank kept the difference as a fee' };

/**
 * "Confirm receipt" for a booking's own payment-schedule row (§8.2's
 * equation read from the row's side, spec §10), filled from the settle
 * preview (OH-193 spec §4.4): the bank amount and the withheld lines live in
 * the settle draft (lib/settleDraft), which the preview fills, and the body
 * carries the draft's base as `expect` (§4.5) — a plan or rate that moved
 * since then is a 409 target_changed and a conflict on screen, never a
 * silent restatement.
 *
 * Two shapes, as before:
 *  - A PSP-fact projection (a capture row, or a refund row of an executed
 *    obligation): the settle amount is FIXED at the fact's own magnitude,
 *    so the bank amount plus the withheld lines must equal it exactly (zero
 *    difference). The provider fee comes as a suggested `provider_fee` line,
 *    converted to the account currency by the server.
 *  - A plain row (payout/vcc_charge/manual): settles like an ordinary
 *    expected target and closes at what was actually paid (OH-205). Below
 *    its plan — in whatever currency — the window asks what about the rest:
 *    the plan was this amount, part came, or, for an incoming row, «the bank
 *    kept the difference as a fee», which asks the server for the shortfall
 *    line and is the answer the window gives itself for guest money until
 *    the operator picks one. A request more than 5% off the plan takes a
 *    second press on Confirm. It counts from the payment amount, as the
 *    queue does (OH-201, OH-202): the operator enters the payment and its
 *    fees as lines — withheld on the way or charged separately by the bank —
 *    and the bank amount follows; typing the bank amount counts from it
 *    instead.
 *
 * Account, currency, date and attachments come from useSettleForm (shared
 * with SettleProviderFactForm, which the preview does not address — §4.1,
 * SP-05); its derived withheld amount is not used here any more.
 */
export function ConfirmReceiptDialog({ open, onClose, bookingId, entry, businessTz }: ConfirmReceiptDialogProps) {
  // Ruling FR-20: only a capture row with its receipt or a refund row of an
  // executed obligation reaches here as a projection.
  const isProjection = entry.method === 'capture' || entry.method === 'refund';
  const planMinor = Math.abs(entry.amount_minor);
  const form = useSettleForm({ isProjection, planMinor, currency: entry.currency, businessTz, plannedOn: entry.expected_at });
  const { toast } = useToast();
  const settle = useSettleScheduleEntry(bookingId);
  const invalidate = useInvalidateFinance();
  const operationIdFor = useSettleOperationId();

  const target: TargetKey = `schedule_entry:${entry.id}`;
  const context = useMemo<DraftContext | null>(
    () =>
      open && form.accountId && form.currency && form.occurredOn
        ? { accountId: form.accountId, currency: form.currency, occurredOn: form.occurredOn, targets: [target] }
        : null,
    [open, form.accountId, form.currency, form.occurredOn, target],
  );
  // A PSP-fact projection keeps its fixed fact and the bank amount; a plain row counts from the payment amount (OH-202).
  const { state, dispatch, preview, refetchAfterConflict } = useSettleDraft({
    context,
    initialSendsBank: false,
    initialAnchor: isProjection ? 'bank' : 'settle',
    bankBeforeOperatorLines: true,
  });
  const [feeMode, setFeeMode] = useState(false);
  // Set by the operator's own answer to the question (chooseMode): from then on the window's default leaves the mode alone.
  const modeTouchedRef = useRef(false);

  const baseTarget = state.base?.expect.targets[0];
  const targetCurrency = baseTarget?.currency ?? entry.currency;
  const remainingMinor = baseTarget?.remaining_minor ?? planMinor;
  const sameCurrency = form.currency === targetCurrency;
  const settleMinor = singleSettleMinor(state, []);
  // What the request sends as amount_minor: typed, or derived from the payment amount (OH-202).
  const bankMinor = singleBankMinor(state, []);
  const countsFromPayment = state.anchor === 'settle';
  const targetMoney = state.targetAmounts[target];
  // The field the form does not count from shows the other one's result.
  const shown = (minor: number | null) => (minor !== null && form.currency ? fromMinor(minor, form.currency) : '');
  // What is off, signed as the draft's difference always was: plus — over. A projection closes at bank + lines
  // = the fact; the fee mode at what arrived + the shortfall line = the plan (feeGapMinor, OH-205 §6) — and says
  // so only for a draft that is ready: beside the conflict or the stale panel the gap is the new plan against old amounts.
  const feeGap = feeGapMinor(state);
  const offByMinor = isProjection ? draftDifferenceMinor(state) : feeMode && feeGap !== null && draftReady(state) ? -feeGap : null;
  // The row's direction as the answer to the draft's own context says it — in either request shape, as the plan is
  // read (plannedMinorOf): the default's own flip of the mode below must not take it away, or the row's sign it would
  // fall back to could send the window back and forth. The row's sign until there is an answer.
  const answered =
    state.suggestion && sameContext(state.suggestion.context, state.context) ? state.suggestion.response.targets[0] : undefined;
  const incoming = answered ? answered.direction === 'in' : entry.amount_minor > 0;
  // Only an incoming plain row may book the shortfall as a bank fee (§4.4 p.6), and not on an
  // account with a fee rule: the server suggests no shortfall there, so the mode could never close.
  const offerFee = !isProjection && incoming && !form.account?.fee_rule;

  // OH-205 (§3.1, §3.3, §4; plan decisions 5, 9, 11): the payment against its plan, in the server's arithmetic
  // (targetComparison) — what the question, the outcome line, the default mode and the second press all read. A provider
  // fact settles at its own amount and is never compared (§5). Nothing is compared while the draft is in a conflict, stale
  // or still empty, or has not caught up with the form (it follows the form's context one effect later, and in the render
  // between, the account the form shows and the answer the draft holds belong to different contexts): its plan and its
  // base are not one answer. A CLEAN draft is compared even before the answer to the request shape it has just switched
  // to lands: the plan does not depend on the shape (plan decision 5), and `draftReady` would take the comparison away
  // from the very flip the default makes below. A payment of zero or below is compared with nothing either: the problem
  // list names it, and nothing else appears beside it.
  const caughtUp = context !== null && sameContext(context, state.context);
  const comparable = !isProjection && caughtUp && state.status === 'clean';
  const compare = (minor: number | null) => (comparable && minor !== null && minor > 0 ? targetComparison(state, target, minor) : null);
  // What the body settles: the question, the outcome line and the second press read it.
  const pay = compare(feeMode ? feeArrivedMinor(state) : settleMinor);
  // What arrived: the default reads it. The same number in either mode — in the fee mode it is `pay`, outside it the two
  // differ by the lines the operator did not enter (a shortfall line they edited stays after the mode ends).
  const arrival = compare(arrivedMinor(state));
  const belowPlan = pay !== null && isHighlighted(pay) && pay.paidMinor < pay.plannedMinor;
  // Asked below the plan, whatever the currency, and while «fee» is chosen: there the picker stays, or there would be
  // no way back once the fee closed the gap.
  const remainderAsk: 'shortfall' | null = isProjection ? null : belowPlan || feeMode ? 'shortfall' : null;
  // What the body carries [PL-R2-01]: «fee» in the fee mode, the operator's answer only while the question shows,
  // otherwise nothing — which the server reads as restate (plan decision 11).
  const mode: Mode = feeMode ? 'fee' : remainderAsk !== null ? form.remainder : 'restate';
  // A schedule row is settled once, and a restate replaces its plan with what was settled (SettlePlainScheduleEntry sets
  // settled_minor, it does not add to it): there is no earlier part to name.
  const outcome = pay ? settleOutcome({ ...pay, mode, settledSoFarMinor: 0 }) : null;
  const outcomeLine = pay && outcome ? outcomeText(pay, outcome, form.currency, targetCurrency, form.currency) : null;

  // OH-205 §3.3: guest money that arrived below the plan is, until the operator answers, the bank keeping the difference.
  // The default reads what ARRIVED against the plan: `arrival` counts neither the mode nor any line the operator did not
  // enter, the plan ignores the request shape (plan decision 5) and so does `offerFee` (the direction is read from the
  // answer in either shape) — flipping the mode changes none of the default's own inputs. It acts only on a known
  // comparison: while there is none (no answer yet, a date or an account just changed, a conflict) the mode stays as it
  // is, or it would flap around every 409 and every account change.
  const wantFee = offerFee && arrival !== null && isHighlighted(arrival) && arrival.paidMinor < arrival.plannedMinor;
  const arrivalKnown = arrival !== null;
  useEffect(() => {
    if (modeTouchedRef.current || !arrivalKnown || wantFee === feeMode) return;
    setFeeMode(wantFee);
    dispatch({ type: 'requestShape', sendsBank: wantFee });
  }, [arrivalKnown, wantFee, feeMode, dispatch]);

  // What the form itself can tell before the draft's admission: the same
  // messages SettleDialog shows; a negative or empty amount never reaches the server.
  const problems: string[] = [];
  if (!form.accountId) problems.push('Choose the account the money landed on.');
  if (countsFromPayment) {
    // In the fee mode the settlement is what arrived plus the shortfall line: unknown when either is, but only
    // a missing «what arrived» is the payment field's problem — a line the draft cannot read is named below. What
    // arrived at zero or below is compared with nothing (`compare`) and is named here too, as the queue window does:
    // the shortfall line, edited to the plan, can keep the settlement above zero. (Counting from the bank it takes
    // care of itself: the lines here are withheld-only, so an arrival of zero or below is a bank amount of zero or below.)
    const arrived = feeMode ? feeArrivedMinor(state) : null;
    const paymentUnknown = feeMode ? arrived === null : settleMinor === null;
    if (paymentUnknown || (arrived !== null && arrived <= 0) || (settleMinor !== null && settleMinor <= 0)) {
      problems.push(`Enter the payment amount, in ${form.currency}.`);
    } else if (bankMinor !== null && bankMinor <= 0) {
      problems.push('The bank amount comes out at zero or below — check the lines.');
    }
  } else if (bankMinor === null || bankMinor <= 0) {
    problems.push(`Enter the amount that hit the account, in ${form.currency}.`);
  }
  if (!form.occurredOn) problems.push('Choose the date the money moved.');
  const amountProblemLines: number[] = [];
  liveLines(state).forEach((l, i) => {
    const amt = toMinor(l.amount.major, form.currency);
    if (amt === null || amt <= 0) {
      problems.push(`Line ${i + 1} needs an amount above zero.`);
      amountProblemLines.push(i);
    }
    if (!l.categoryId) problems.push(`Line ${i + 1} needs an article.`);
  });
  // The fee mode's settlement reads the shortfall line too. One the draft cannot read although its text parses (typed for
  // another account's currency) makes the settlement unknown all the same, and no problem above names it: name it here.
  // Only while no line already has an amount problem listed — such a line is named once, above.
  if (feeMode && settleMinor === null && feeArrivedMinor(state) !== null && amountProblemLines.length === 0) {
    const i = liveLines(state).findIndex((l) => l.source === 'shortfall');
    if (i >= 0) problems.push(`Line ${i + 1} can’t be read — enter its amount again.`);
  }
  // `caughtUp`: in the render before the draft follows the form's account, currency or date, the form shows the new one over
  // the old draft's lines and base — Confirm waits for the draft, so that the body, the comparison and the second press are
  // always one draft's, by construction (no input can reach that render).
  const canConfirm =
    caughtUp &&
    problems.length === 0 &&
    canSubmitSingle(state, { isProjection, feeMode, dependentsMinor: [], settledOverride: false });

  // The body Confirm would send, built once per render — the second press compares its fingerprint, submit sends it.
  // Only a form that can be confirmed has one; `canConfirm` needs a ready draft, so no fingerprint exists otherwise.
  const content: Omit<FinanceSettleRequest, 'operation_id'> | null =
    canConfirm && bankMinor !== null && state.base
      ? {
          account_id: form.accountId,
          currency: form.currency,
          amount_minor: bankMinor,
          occurred_on: form.occurredOn,
          lines: draftLineInputs(state),
          attachment_ids: form.receipts.map((r) => r.id),
          expect: state.base.expect,
          ...(!feeMode && remainderAsk !== null ? { remainder: form.remainder } : {}),
        }
      : null;
  // OH-205 §4: more than 5% off the plan — and the first press only arms the button. `pressing` reads the amounts and the
  // lines, which are in the body; the plan and the rates they are compared with are a function of `expect`, which is in the
  // body too. A line's `source` is not in the body, and that cannot skip a press: swapping a shortfall line for an operator
  // line takes a remove and an add, two fingerprint changes.
  const pressing = pay !== null && needsSecondPress(pay) ? [pay] : [];
  const twice = useConfirmTwice(content && JSON.stringify(content), pressing.length > 0);

  // Keep / Apply / Reset settle the question the 409 message pointed at.
  const resolve = (e: DraftEvent) => {
    form.setError(null);
    dispatch(e);
  };

  function leaveFeeMode() {
    setFeeMode(false);
    dispatch({ type: 'requestShape', sendsBank: false });
  }

  function chooseMode(v: Mode) {
    modeTouchedRef.current = true;
    if (v === 'fee') {
      setFeeMode(true);
      dispatch({ type: 'requestShape', sendsBank: true });
      return;
    }
    form.setRemainder(v);
    leaveFeeMode();
  }

  function submit() {
    form.setError(null);
    if (!content || !twice.press()) return;
    const body: FinanceSettleRequest = { operation_id: operationIdFor(content), ...content };
    settle.mutate(
      { entryId: entry.id, body },
      {
        onSuccess: () => {
          toast({ kind: 'success', title: 'Receipt confirmed' });
          onClose();
        },
        onError: (err) => {
          const code = errorCode(err);
          if (code === 'target_changed') {
            // the plan or a rate moved since the suggestion: ask again, the draft goes into a conflict (S3)
            refetchAfterConflict();
            form.setError('The plan or an exchange rate changed since the suggestion — compare them below.');
          } else if (code === 'operation_conflict') {
            invalidate(bookingId);
            form.setError(OPERATION_CONFLICT_MESSAGE);
          } else if (code === 'income_side_disabled') {
            form.setError('Confirming receipts turns on once the finance-block cutover runs.');
          } else {
            form.setError(errorMessage(err));
          }
        },
      },
    );
  }

  const modeOptions = [...SETTLE_REMAINDER_OPTIONS, ...(offerFee ? [FEE_OPTION] : [])];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      side
      title="Confirm receipt"
      description={`${entry.method} · ${formatMoney(entry.amount_minor, entry.currency)}`}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={settle.isPending}>
            Cancel
          </Button>
          <Button variant={twice.armed ? 'gold' : 'primary'} onClick={submit} loading={settle.isPending} disabled={!canConfirm}>
            {twice.armed ? `Confirm — ${offPlanText(pressing)}` : 'Confirm'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Select
          label="Account"
          value={form.accountId}
          options={form.accounts.filter((a) => a.is_active).map((a) => ({ value: a.id, label: a.name }))}
          onChange={(e) => {
            const next = form.accounts.find((a) => a.id === e.target.value);
            form.setAccountId(e.target.value);
            form.setCurrency(next?.currencies.includes(entry.currency) ? entry.currency : (next?.currencies[0] ?? entry.currency));
            // An account with a fee rule has no fee mode (OH-202): the mode ends here as it does when any other answer is
            // picked — hiding the radio alone would leave the request asking for a shortfall.
            if (feeMode && next?.fee_rule) leaveFeeMode();
          }}
        />
        <Select
          label="Currency"
          value={form.currency}
          options={(form.account?.currencies ?? [entry.currency]).map((c) => ({ value: c, label: c }))}
          onChange={(e) => form.setCurrency(e.target.value)}
        />
        <Input label="Date" type="date" value={form.occurredOn} onChange={(e) => form.setOccurredOn(e.target.value)} required />

        <DraftConflictPanel state={state} dispatch={resolve} />
        <DraftStalePanel state={state} dispatch={resolve} />

        {isProjection ? (
          <MoneyInput
            label="Bank amount"
            currency={form.currency || 'EUR'}
            value={state.bank.major}
            onChange={(major) => dispatch({ type: 'editBank', major })}
            error={staleFieldError(state.bank, state.context.currency)}
            disabled={!context}
            required
          />
        ) : (
          <MoneyInput
            label="Payment amount"
            currency={form.currency || 'EUR'}
            // [PL-01] In the fee mode the field is always what arrived — never the settlement (what arrived
            // plus the shortfall): typing the number it shows must not change the money.
            value={countsFromPayment ? (targetMoney?.major ?? '') : shown(feeMode ? feeArrivedMinor(state) : settleMinor)}
            onChange={(major) => dispatch({ type: 'editSettle', major })}
            error={countsFromPayment && targetMoney ? staleFieldError(targetMoney, state.context.currency) : undefined}
            hint={
              feeMode
                ? 'What reached the account — a fee your bank charged separately goes in as a line below.'
                : incoming
                  ? 'The payment before its fees — the ones withheld on the way and the ones your bank charged separately go in as lines below.'
                  : 'What the payment itself came to — its fees go in as lines below.'
            }
            disabled={!context}
            required
          />
        )}

        <DraftLinesEditor
          state={state}
          dispatch={dispatch}
          currency={form.currency}
          kinds={['withheld']}
          articlesFor={() => form.feeCategories}
          allCategories={form.categories}
          disabled={!context}
        />

        {!isProjection && (
          <MoneyInput
            label="Bank amount"
            currency={form.currency || 'EUR'}
            value={countsFromPayment ? shown(bankMinor) : state.bank.major}
            onChange={(major) => dispatch({ type: 'editBank', major })}
            error={countsFromPayment ? undefined : staleFieldError(state.bank, state.context.currency)}
            hint="What the account ends up with: the payment less the lines. Change it and the payment amount follows."
            disabled={!context}
            required
          />
        )}

        {settleMinor !== null && sameCurrency && (
          <p className="text-sm text-muted" data-testid="settle-preview">
            Settles <span className="tabular-nums text-foreground">{formatMinor(settleMinor, form.currency)}</span> of{' '}
            <span className="tabular-nums text-foreground">{formatMinor(remainingMinor, form.currency)}</span> remaining
            {settleMinor > remainingMinor && <> — the plan will be restated to {formatMinor(settleMinor, form.currency)}</>}
          </p>
        )}
        {offByMinor !== null && offByMinor !== 0 && (
          <p className="text-sm text-[var(--ollio-gold-deep)]" data-testid="settle-difference">
            Off by {formatMinor(offByMinor, form.currency)}:{' '}
            {isProjection
              ? 'the bank amount plus the withheld lines must equal the fact.'
              : 'what arrived and the shortfall line must add up to the plan.'}
          </p>
        )}

        {/* OH-205 §4: whenever the fact differs from the plan — what Confirm does about it, where the amounts are. */}
        {outcomeLine && (
          <p className="text-sm font-medium text-[var(--ollio-gold-deep)]" data-testid="settle-outcome">
            {outcomeLine}
          </p>
        )}

        {remainderAsk !== null && (
          <fieldset className="m-0 flex flex-col gap-2 border-0 p-0">
            <legend className="text-sm font-semibold text-[var(--text-heading)]">Below plan — what about the rest?</legend>
            <RadioGroup options={modeOptions} value={feeMode ? 'fee' : form.remainder} onChange={(v) => chooseMode(v as Mode)} />
          </fieldset>
        )}

        <DraftSuggestionInfo state={state} preview={preview} />
        {problems.length > 0 && (
          <ul className="flex flex-col gap-0.5 text-sm text-[var(--ollio-gold-deep)]" data-testid="settle-problems">
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        )}

        <div>
          <p className="mb-1 text-xs font-semibold uppercase tracking-[0.06em] text-muted">Receipts</p>
          <ReceiptUploadButton
            target={{}}
            onUploaded={(id, file) => {
              form.setUploadError(null);
              form.setReceipts((rs) => [...rs, { id, name: file.name }]);
            }}
            onError={form.setUploadError}
          />
          <AttachmentList
            className="mt-2 flex flex-col gap-1"
            items={form.receipts}
            onRemove={(id) => form.setReceipts((rs) => rs.filter((r) => r.id !== id))}
          />
          {form.uploadError && (
            <p role="alert" className="mt-1 text-sm text-destructive">
              {form.uploadError}
            </p>
          )}
        </div>

        {form.error && (
          <p role="alert" className="text-sm text-destructive">
            {form.error}
          </p>
        )}
      </div>
    </Dialog>
  );
}
